import type { InvoiceLinePayload, InvoiceRejectionReason } from '../types/agreements.types';
import type { SelectionSummaryRow } from './invoiceSelection';

export type RejectionTarget = 'over_balance' | 'code' | 'submit';

export type MessagesByTarget = Record<RejectionTarget, string[]>;

export interface InvoiceRejectionMessages {
    byTarget: MessagesByTarget;
    byLineKey: Map<string, string>;
    fieldErrors: Record<string, string[]>;
}

const REASON_COPY: Record<string, string> = {
    agreement_not_active: 'This agreement is not active. Invoicing cannot proceed.',
    agreement_not_invoiceable: "This plan can't be invoiced in its current state.",
    line_not_found: 'One of the selected lines could not be found on this agreement.',
    line_invoiced_with_service: 'This line can only be invoiced as part of its service.',
    service_priced_whole: 'This service was sold at a single price and can only be invoiced whole.',
    line_already_invoiced: 'One of the selected lines has already been invoiced.',
    duplicate_line: 'One of the selected lines was submitted more than once.',
    service_has_no_items: 'This service has no items to invoice.',
    invoicing_dealer_not_allowed: 'You cannot invoice for the selected dealer.',
    invoice_number_taken: 'This invoice number has already been used for the selected dealer.',
    selection_exceeds_balance: 'This selection exceeds the account balance on this agreement.',
    selection_within_balance: 'This selection is within the account balance, so no authorisation is needed.',
    pot_empty: 'The pot is empty, so there is nothing to claim. Choose another way to fund this.',
    funding_approval_invalid: 'The approval was not accepted. Check the code, or authorise the excess again.',
    funding_approval_superseded:
        'The balance has changed and the excess is now larger than was authorised. Authorise it again.',
};

const REASON_TARGET: Record<string, RejectionTarget> = {
    funding_approval_invalid: 'code',
    funding_approval_superseded: 'over_balance',
    selection_exceeds_balance: 'over_balance',
    pot_empty: 'over_balance',
};

const GENERIC_MESSAGE = 'This invoice was not accepted.';

export const emptyTargets = (): MessagesByTarget => ({ over_balance: [], code: [], submit: [] });

export const potEmptyMessage = (reasons: InvoiceRejectionReason[]): string | null =>
    reasons.some((reason) => reason.line === null && reason.code === 'pot_empty') ? REASON_COPY.pot_empty : null;

export function withAgreementRejection(byTarget: MessagesByTarget, message: string | null): MessagesByTarget {
    if (message === null || byTarget.over_balance.includes(message)) return byTarget;
    return { ...byTarget, over_balance: [...byTarget.over_balance, message] };
}

function messageFor(code: string): string {
    return REASON_COPY[code] ?? GENERIC_MESSAGE;
}

function findRowKeyForLine(line: InvoiceLinePayload, rows: SelectionSummaryRow[]): string | undefined {
    if (line.kind === 'service') {
        return rows.find((row) => row.key === `service:${line.agreement_service_id}`)?.key;
    }
    return rows.find(
        (row) =>
            (row.key.startsWith('item:') || row.key.startsWith('add_on:')) &&
            row.key.endsWith(`:${line.agreement_item_id}`),
    )?.key;
}

/** Maps each 422 reason to copy and to the control that fixes it; a line-indexed reason
 *  names its summary row, degrading to its reason's target when the row can't be resolved. */
export function invoiceRejectionMessages(
    reasons: InvoiceRejectionReason[],
    lines: InvoiceLinePayload[],
    rows: SelectionSummaryRow[],
): InvoiceRejectionMessages {
    const byTarget = emptyTargets();
    const byLineKey = new Map<string, string>();
    const fieldErrors: Record<string, string[]> = {};

    for (const reason of reasons) {
        const message = messageFor(reason.code);
        const line = reason.line !== null ? lines[reason.line] : undefined;
        const rowKey = line ? findRowKeyForLine(line, rows) : undefined;

        if (reason.code === 'invoice_number_taken') {
            fieldErrors.invoice_number = [...(fieldErrors.invoice_number ?? []), message];
        } else if (rowKey) {
            byLineKey.set(rowKey, message);
        } else {
            byTarget[REASON_TARGET[reason.code] ?? 'submit'].push(message);
        }
    }

    return { byTarget, byLineKey, fieldErrors };
}

/** A row dropped from the selection while the request was in flight has nowhere to
 *  render its line message, so that message moves above Submit instead. */
export function promoteUnrenderableLineMessages(
    messages: InvoiceRejectionMessages,
    liveRowKeys: Set<string>,
): InvoiceRejectionMessages {
    const byTarget: MessagesByTarget = { ...messages.byTarget, submit: [...messages.byTarget.submit] };
    const byLineKey = new Map<string, string>();

    for (const [rowKey, message] of messages.byLineKey) {
        if (liveRowKeys.has(rowKey)) byLineKey.set(rowKey, message);
        else byTarget.submit.push(message);
    }

    return { byTarget, byLineKey, fieldErrors: messages.fieldErrors };
}

export interface RenderedTargets {
    overBalance: boolean;
    codeField: boolean;
}

/** A message whose target is not on screen falls back: code field, then the over-balance section, then Submit. */
export function placeRejections(byTarget: MessagesByTarget, rendered: RenderedTargets): MessagesByTarget {
    const codeShown = rendered.codeField && rendered.overBalance;
    const code = codeShown ? byTarget.code : [];
    const overBalance = [...(codeShown ? [] : byTarget.code), ...byTarget.over_balance];
    const placedOverBalance = rendered.overBalance ? overBalance : [];
    const stranded = rendered.overBalance ? [] : overBalance;

    return {
        code,
        over_balance: placedOverBalance,
        submit: [...stranded, ...byTarget.submit],
    };
}
